import { Injectable, Output, EventEmitter } from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { LooseModuleData } from '../module/form-templates/shared/contracts/loose-module-data';
import { Select as DropdownOption } from '../objects/select';
import { Router } from '@angular/router';
import { data_get, filled, OptionalValue, spf } from '../shared/utils/common';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';

@Injectable()
export class ViewService {

  public arRecordConfig: string[] = [];

  public strRecordModule: string;

  public arRecord: any;

  private obvViewResult = new Subject<any>();
  obvViewResult$ = this.obvViewResult.asObservable();

  private callModuleRecordView = new Subject<any>();
  moduleRecordViewCalled$ = this.callModuleRecordView.asObservable();

  @Output() reloadRecordDetail = new EventEmitter<any>();
  @Output() arUpdateStatusWidget = new EventEmitter<any>();
  @Output() arStatusWidget = new EventEmitter<any>();
  @Output() strStatusValue = new EventEmitter<any>();
  @Output() bStatusWidgetEditable = new EventEmitter<boolean>();

  public objRecord;
  public strRecordNumber = '';

  private readonly _details$ = new Subject<OptionalValue<ViewedRecordDetails>>();
  readonly details$ = this._details$.asObservable().pipe(
    debounceTime(100),
    distinctUntilChanged(),
  );

  private readonly _tab$ = new Subject<OptionalValue<string>>();
  readonly tab$ = this._tab$.asObservable();

  readonly isHistory$ = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private readonly _title: Title,
  ) { }

  /**
   * Set and get job records
   *
   * @param strRecordId - job id
   */
  setViewRecord (arRecord: any = []){
    this.arRecord = arRecord;
  }

  getViewRecord () {
    return this.arRecord;
  }

  /**
   * Set record name
   *
   * @param strRecordId
   */
  setRecordNumber(strNumber: string){
    this.strRecordNumber = strNumber;
  }

  getRecordNumber() {
    return this.strRecordNumber;
  }

  /**
   * Set record module
   *
   * @param strRecordId
   */
  setViewResult(objResponse: any){
    this.objRecord = objResponse;
    this.obvViewResult.next(objResponse);

    const details = data_get(objResponse, 'record_details');

    this._details$.next(details);

    if (filled(details) && filled(data_get(details, 'text'))) {
      let text = data_get(details, 'text');

      if (/^[0-9]/.test(text)) {
        text = spf('#%s', {
          args: [text],
        });
      }

      this._title.setTitle(spf('%s %s', {
        args: [
          this._title.getTitle(),
          text,
        ],
      }))
    }
  }

  getRecord() {
    return this.objRecord;
  }

  /**
   * Set record module
   *
   * @param strRecordId
   */
  setRecordConfig(arRecordConfig: any){
    this.arRecordConfig = arRecordConfig;
  }

  /**
   * Set record module
   *
   * @param strRecordModule
   */
  setRecordModule(strRecordModule: string) {
    this.strRecordModule = strRecordModule;
  }

  /**
   * Call module record view
   *
   */
  callRecordView() {
    this.callModuleRecordView.next();
  }
  /**
   * Set status widget value
   *
   * @param arData
   */
  updateStatusWidget(arData: any) {
    this.arUpdateStatusWidget.emit(arData);
  }

  /**
   * Set status widget value
   *
   * @param arData
   */
  reloadRecordView(reloadRecordDetail: any) {
    this.reloadRecordDetail.emit(reloadRecordDetail);
  }

  /**
   * Set status widget value
   *
   * @param arData
   */
  setStatusWidget(arData: any) {
    this.arStatusWidget.emit(arData);
  }

  /**
   * Update the current status widget value
   *
   * @param arData
   */
  updateStatusWidgetValue(strValue: any) {
    this.strStatusValue.emit(strValue);
  }

  /**
   * wraps the property obvViewResult$ this will make the other service/components testable
   * since it's kinda hard to mock/stub a property of a class
   *
   * @returns {Observable<ModuleViewResult>}
   */
  getModuleViewResult$(): Observable<ModuleViewResult> {
    return this.obvViewResult$;
  }

  onModuleRecordView$(): Observable<undefined> {
    return this.moduleRecordViewCalled$;
  }

  makeRedirectUrl = (moduleName: string, id: string, opts: MakeRedirectUrlOpts = {}): string => this.router.createUrlTree([
    ... (opts.isAdmin ? ['admin'] : []),
    moduleName,
    id,
  ], {
    queryParams: {
      ... opts.queryParams,
      ... (filled(opts.openWidgetTab) && {
        widget_tab: opts.openWidgetTab,
      }),
    },
  }).toString();

  setTab = (name: string) => this._tab$.next(name);

  setHistoryToggle = (value: boolean = false) => this.isHistory$.next(value);

  /**
   * to determine if status widget is editable
   */
  setStatusWidgetEditable = (isEditable: boolean) => this.bStatusWidgetEditable.emit(isEditable);
}

export interface ModuleUsedField {
  type: 'relate' | 'datetime' | 'date' | 'dropdown' | 'currency' | 'text' | 'textarea'
    | 'number' | 'address' | 'checkbox' | 'multiselect' | 'increment' | 'dialog' | 'json'
    | 'array' | 'images' | 'image';
  label: string;
  default_value?: any;
  module?: string;
  options?: DropdownOption[];
  format?: {[ key: string]: ModuleUsedField};
  decimals?: number;
}

export interface ModuleViewResult {
  /**
   * this contains the current module data
   *
   * @var {LooseModuleData}
   */
  record_details: LooseModuleData;

  /**
   * this contains the fields that is used
   *
   * @var {[key: string]: ModuleUsedField}
   */
  used_fields?: {[key: string]: ModuleUsedField},

  /**
   * this contains the fields that can be used in the view and not being rendered
   *
   * @var {[key: string]: ModuleUsedField}
   */
  available_fields?: {[key: string]: ModuleUsedField},

  /**
   * this contains the list of related data that the module has
   */
  related_data?: [{
    [key: string]: any,
  }]
}

type MakeRedirectUrlOpts = {
  /**
   * Contains the additional query params
   */
  queryParams?: Record<string, any>;

  /**
   * If the view url is meant for admin. The will add a base `admin` path to the
   * generated url.
   *
   * Example: 'admin/foo/12345'
   */
  isAdmin?: boolean;

  /**
   * Adds a widget_tab query param to the generated url. This is useful when you want to
   * to imediately open a widget tab after the view is loaded.
   */
  openWidgetTab?: string;
}

export type ViewedRecordDetails<T = Record<string, any>> = {
  id: string;
} & T;

export type ViewRecordResponseData<T = Record<string, any>> = {
  record_details: ViewRecordResponseData<T>;
}